import axios from "axios";
import React from "react";
import { useMap } from "react-map-gl";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { selectedFocusAreaAtom, selectedRegionESRIGeometrySelector } from "../../../map/selectedRegionAtoms"
import { popupAtom } from "../../../popups/popupAtom";

import {
	layerDefVisibilitySelector,
} from "../../recoil";

import {
	visitorAndSpendTypeAtom,
	visitorAndSpendOptionsAtom,
	visitorAndSpendGridCacheAtom,
} from './atoms'

import { gis2TokenAtom } from '../../../login/loginAtoms.js'

import VisitorAndSpendTypeSelector from './VisitorAndSpendTypeSelector.jsx'
import VisitorAndSpendDaySelector from './VisitorAndSpendDaySelector.jsx'
import VisitorAndSpendTimeSlotSelector from './VisitorAndSpendTimeSlotSelector.jsx'
import VisitorAndSpendLegend from './VisitorAndSpendLegend.jsx'

export default function VisitorAndSpendControls() {
	const mapRef = useMap()
	const selectedFocusArea = useRecoilValue(selectedFocusAreaAtom)
	const selectedRegionESRIGeometry = useRecoilValue(selectedRegionESRIGeometrySelector)
	const visitorAndSpendType = useRecoilValue(visitorAndSpendTypeAtom)
	const visitorAndSpendOptions = useRecoilValue(visitorAndSpendOptionsAtom)
	const setVisitorAndSpendGridCache = useSetRecoilState(visitorAndSpendGridCacheAtom)
	const visitorAndSpendLayerVisibility = useRecoilValue(layerDefVisibilitySelector('visitor-and-spend'))
	const gis2Token = useRecoilValue(gis2TokenAtom)
	const setPopup = useSetRecoilState(popupAtom);

	const createGridURL = () => {
    return visitorAndSpendType.gridService + "/query?" + new URLSearchParams({
      where: "1=1",
      geometry: JSON.stringify(selectedRegionESRIGeometry),
      geometryType: "esriGeometryPolygon",
      outFields: "*",
      f: "geojson",
    })
	}

	const createDataRequest = (gridIds) => {
		return {
			url: visitorAndSpendType.dataService + "/query",
			params: {
	      where: generateSQL(
	      	visitorAndSpendOptions,
	      	visitorAndSpendType.gridIdField,
	      	gridIds,
	      ),
	      returnGeometry: false,
	      outFields: "*",
	      f: "json",
	      token: gis2Token,
	    }
		}
	}

	const identifyGridIds = (gridFeatures) => {
		return gridFeatures.map((f) => {
			return f.properties[visitorAndSpendType.gridIdField]
		})
	}

	const generateSQL = (options, gridIdField, gridIds) => {
		const timeSlot = visitorAndSpendType.formatTimeSlot(options.time_slot)
	  return `day='${options.day}' AND time_slot='${timeSlot}' AND ${gridIdField} IN (${gridIds.join(',')})`
	}

	const mergeDataAttrIntoGridProps = (gridIds, gridGeojson, dataAsPJson) => {
		const results = structuredClone(gridGeojson)
		const attrSets = dataAsPJson.features.map((f) => f.attributes)

		// TODO: all this could be much more performant and concise.

/*
		for (const attrs of attrSets) {
			const gridId = attrs[visitorAndSpendType.gridIdField]
			
			const gridFeature = results.features.find((f) => {
				return f.properties[visitorAndSpendType.gridIdField] === gridId
			})

			gridFeature.properties = {
				...gridFeature.properties,
				...attrs,
			}
		}
*/
		for (const id of gridIds) {
			const attrs = attrSets.find((attrs) => {
				return attrs[visitorAndSpendType.gridIdField] === id
			})

			const gridFeature = results.features.find((f) => {
				return f.properties[visitorAndSpendType.gridIdField] === id
			})

			if (attrs) {
				gridFeature.properties = {
					...gridFeature.properties,
					...attrs,
				}
			} else {
				gridFeature.properties = {
					...gridFeature.properties,
					...visitorAndSpendType.emptyCellProps,
					time_slot: visitorAndSpendType.formatTimeSlot(visitorAndSpendOptions.time_slot),
					day: visitorAndSpendOptions.day,
				}
			}
		}

		return results
	}

	React.useEffect(() => {
		if (!mapRef.map) {
			return
		}

		if (!gis2Token) {
			return		
		}

		if (!visitorAndSpendLayerVisibility) {
			return
		}

		const map = mapRef.map
		const gridURL = createGridURL()
		let gridGeojson = null
		let gridIds = []
		let cancelDataUpdate = false

		setPopup(null);

		axios.get(gridURL)
		.then((res) => {
			if (cancelDataUpdate) {
				return
			}

			// The endpoint returns 200 status even if there is an error ¯\_(ツ)_/¯
			// Must check for presence of 'res.data.error' instead
			if (res.data.error) {
				throw new Error(res.data.error)
			}

			gridGeojson = res.data

			setVisitorAndSpendGridCache({
				key: {
					id: visitorAndSpendType.id,
					hsLayer: selectedFocusArea.properties.layer,
					hsName: selectedFocusArea.properties.name,
				},
				geojson: gridGeojson,
			})

			// Add hex grid early so user know something is happening
			map.getSource('visitor-and-spend').setData(gridGeojson)

			return gridGeojson
		})
		.then((gridGeojson) => {
			if (!gridGeojson) {
				return
			}

			gridIds = identifyGridIds(gridGeojson.features)
			const dataRequest = createDataRequest(gridIds)

			return axios.post(
				dataRequest.url,
				dataRequest.params,
				{
				 	headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
			   	}
	    	}
    	)
		})
		.then((res) => {
			if (cancelDataUpdate) {
				return
			}

			// The endpoint returns 200 status even if there is an error ¯\_(ツ)_/¯
			// Must check for presence of 'res.data.error' instead
			if (res.data.error) {
				throw new Error(res.data.error)
			}

			gridGeojson = mergeDataAttrIntoGridProps(gridIds, gridGeojson, res.data)
			map.getSource('visitor-and-spend').setData(gridGeojson)
		})
		.catch(console.error)

		return () => {
			cancelDataUpdate = true
		}
	}, [visitorAndSpendType, visitorAndSpendOptions, visitorAndSpendLayerVisibility, selectedRegionESRIGeometry])

	return (
		<>
			<VisitorAndSpendTypeSelector />
			<VisitorAndSpendDaySelector />
			<VisitorAndSpendTimeSlotSelector />
			<VisitorAndSpendLegend />
		</>
	)
}